/** The languages this build ships. Adding one is a decision, not an inference */
export const languages = ['en', 'es'] as const;
export type Language = (typeof languages)[number];

/** A flat map of stable keys to text */
export type Entries = Record<string, string>;

/**
 * The text of one language, with an explicit fallback and an explicit account of what is missing: a
 * key the catalog does not hold falls back to English, and if English does not hold it either the
 * reader gets an em dash rather than a raw key. What fell back is recorded. Only interface text lives
 * here; identifiers, digests and anything a person typed are never translated.
 */
export class Catalog {
	#language: Language;
	get language() {
		return this.#language;
	}

	#entries: Entries;
	#fallback: Entries;
	#missing = new Set<string>();
	get missing(): string[] {
		return [...this.#missing].sort();
	}

	constructor(language: Language, entries: Entries, fallback: Entries) {
		this.#language = language;
		this.#entries = entries;
		this.#fallback = fallback;
	}

	text(key: string, values: Record<string, string | number> = {}): string {
		let text = this.#entries[key];
		if (text === undefined) {
			this.#missing.add(key);
			text = this.#fallback[key];
		}
		if (text === undefined) return '—';
		return text.replace(/\{(\w+)\}/g, (_, name: string) => String(values[name] ?? ''));
	}

	/**
	 * A count in words, by the language's plural rules: the key's `.one` form for one, `.other` otherwise,
	 * with `{count}` filled. Never "(s)"
	 */
	count(key: string, count: number, values: Record<string, string | number> = {}): string {
		const form = new Intl.PluralRules(this.#language).select(count) === 'one' ? 'one' : 'other';
		return this.text(`${key}.${form}`, { ...values, count: count.toLocaleString(this.#language) });
	}

	static #units: [Intl.RelativeTimeFormatUnit, number][] = [['year', 31536000000], ['month', 2592000000], ['week', 604800000], ['day', 86400000], ['hour', 3600000], ['minute', 60000], ['second', 1000]];

	/** How far a moment is from now, in the reader's language: "3 minutes ago", "in 2 days", "now" */
	relative(value: string | Date | undefined, now = Date.now()): string {
		if (!value) return '—';
		const time = (value instanceof Date ? value : new Date(value)).getTime();
		if (Number.isNaN(time)) return '—';
		const distance = time - now;
		const format = new Intl.RelativeTimeFormat(this.#language, { numeric: 'auto' });
		for (const [unit, length] of Catalog.#units) if (Math.abs(distance) >= length) return format.format(Math.round(distance / length), unit);
		return format.format(0, 'second');
	}

	/** A moment of the day, in the reader's language: "15:53" or "3:53 PM" */
	time(value: string | Date | undefined): string {
		if (!value) return '—';
		const date = value instanceof Date ? value : new Date(value);
		return Number.isNaN(date.getTime()) ? '—' : date.toLocaleTimeString(this.#language === 'es' ? 'es' : 'en', { timeStyle: 'short' });
	}

	/** A moment without its year, in the reader's language: "Oct 17, 3:53 PM" */
	short(value: string | Date | undefined): string {
		if (!value) return '—';
		const date = value instanceof Date ? value : new Date(value);
		return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString(this.#language === 'es' ? 'es' : 'en', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
	}

	/** A moment, in the reader's language */
	date(value: string | Date | undefined): string {
		if (!value) return '—';
		const date = value instanceof Date ? value : new Date(value);
		return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString(this.#language === 'es' ? 'es' : 'en', { dateStyle: 'medium', timeStyle: 'short' });
	}

	/** A size in bytes, for a person, in decimal units as quotas are stated (5 GB is 5,000,000,000 bytes) */
	size(bytes: number | undefined): string {
		if (bytes === undefined) return '—';
		if (bytes < 1000) return `${bytes} B`;
		const [unit, length] = ([['GB', 1e9], ['MB', 1e6], ['KB', 1e3]] as const).find(([, step]) => bytes >= step)!;
		return `${(bytes / length).toFixed(1)} ${unit}`;
	}
}
